import {
  Database,
  Zap,
  DatabaseZap,
  Filter,
  ShieldCheck,
  Search,
  BarChart3,
  Globe,
  Upload,
  Bell,
  GitBranch,
  Clock,
  Code,
  Workflow,
  Sparkles,
  Bot,
  GitMerge,
  UserCheck,
  Wrench,
  BookOpen,
  BookPlus,
  Network,
  GitFork,
  ScanSearch,
  Webhook,
  type LucideIcon,
} from "lucide-react";
import type { StepType, WorkflowCatalogItemType } from "@/types/workflow.types";

export interface StepTypeConfig {
  icon: LucideIcon;
  label: string;
  color: string;
  bgColor: string;
  darkBgColor: string;
  iconColor: string;
  borderColor: string;
}

export const stepTypeConfig: Record<StepType, StepTypeConfig> = {
  extract: {
    icon: Database,
    label: "Extract",
    color: "blue",
    bgColor: "bg-blue-50",
    darkBgColor: "dark:bg-blue-950/40",
    iconColor: "text-blue-600 dark:text-blue-400",
    borderColor: "border-blue-400 dark:border-blue-600",
  },
  transform: {
    icon: Zap,
    label: "Transform",
    color: "amber",
    bgColor: "bg-amber-50",
    darkBgColor: "dark:bg-amber-950/40",
    iconColor: "text-amber-600 dark:text-amber-400",
    borderColor: "border-amber-400 dark:border-amber-600",
  },
  load: {
    icon: DatabaseZap,
    label: "Load",
    color: "green",
    bgColor: "bg-green-50",
    darkBgColor: "dark:bg-green-950/40",
    iconColor: "text-green-600 dark:text-green-400",
    borderColor: "border-green-400 dark:border-green-600",
  },
  filter: {
    icon: Filter,
    label: "Filter",
    color: "orange",
    bgColor: "bg-orange-50",
    darkBgColor: "dark:bg-orange-950/40",
    iconColor: "text-orange-600 dark:text-orange-400",
    borderColor: "border-orange-400 dark:border-orange-600",
  },
  validate: {
    icon: ShieldCheck,
    label: "Validate",
    color: "teal",
    bgColor: "bg-teal-50",
    darkBgColor: "dark:bg-teal-950/40",
    iconColor: "text-teal-600 dark:text-teal-400",
    borderColor: "border-teal-400 dark:border-teal-600",
  },
  lookup: {
    icon: Search,
    label: "Lookup",
    color: "purple",
    bgColor: "bg-purple-50",
    darkBgColor: "dark:bg-purple-950/40",
    iconColor: "text-purple-600 dark:text-purple-400",
    borderColor: "border-purple-400 dark:border-purple-600",
  },
  aggregate: {
    icon: BarChart3,
    label: "Aggregate",
    color: "indigo",
    bgColor: "bg-indigo-50",
    darkBgColor: "dark:bg-indigo-950/40",
    iconColor: "text-indigo-600 dark:text-indigo-400",
    borderColor: "border-indigo-400 dark:border-indigo-600",
  },
  api_extract: {
    icon: Globe,
    label: "API Extract",
    color: "cyan",
    bgColor: "bg-cyan-50",
    darkBgColor: "dark:bg-cyan-950/40",
    iconColor: "text-cyan-600 dark:text-cyan-400",
    borderColor: "border-cyan-400 dark:border-cyan-600",
  },
  api_load: {
    icon: Upload,
    label: "API Load",
    color: "emerald",
    bgColor: "bg-emerald-50",
    darkBgColor: "dark:bg-emerald-950/40",
    iconColor: "text-emerald-600 dark:text-emerald-400",
    borderColor: "border-emerald-400 dark:border-emerald-600",
  },
  http_request: {
    icon: Globe,
    label: "HTTP Request",
    color: "sky",
    bgColor: "bg-sky-50",
    darkBgColor: "dark:bg-sky-950/40",
    iconColor: "text-sky-600 dark:text-sky-400",
    borderColor: "border-sky-400 dark:border-sky-600",
  },
  notify: {
    icon: Bell,
    label: "Notify",
    color: "rose",
    bgColor: "bg-rose-50",
    darkBgColor: "dark:bg-rose-950/40",
    iconColor: "text-rose-600 dark:text-rose-400",
    borderColor: "border-rose-400 dark:border-rose-600",
  },
  conditional: {
    icon: GitBranch,
    label: "Conditional",
    color: "violet",
    bgColor: "bg-violet-50",
    darkBgColor: "dark:bg-violet-950/40",
    iconColor: "text-violet-600 dark:text-violet-400",
    borderColor: "border-violet-400 dark:border-violet-600",
  },
  delay: {
    icon: Clock,
    label: "Delay",
    color: "gray",
    bgColor: "bg-gray-50",
    darkBgColor: "dark:bg-gray-800",
    iconColor: "text-gray-600 dark:text-gray-400",
    borderColor: "border-gray-400 dark:border-gray-600",
  },
  code: {
    icon: Code,
    label: "Code",
    color: "slate",
    bgColor: "bg-slate-50",
    darkBgColor: "dark:bg-slate-900/40",
    iconColor: "text-slate-600 dark:text-slate-400",
    borderColor: "border-slate-400 dark:border-slate-600",
  },
  subworkflow: {
    icon: Workflow,
    label: "Sub-workflow",
    color: "pink",
    bgColor: "bg-pink-50",
    darkBgColor: "dark:bg-pink-950/40",
    iconColor: "text-pink-600 dark:text-pink-400",
    borderColor: "border-pink-400 dark:border-pink-600",
  },

  // Agentic step types
  llm_call: {
    icon: Sparkles,
    label: "LLM Call",
    color: "purple",
    bgColor: "bg-purple-50",
    darkBgColor: "dark:bg-purple-950/40",
    iconColor: "text-purple-600 dark:text-fuchsia-400",
    borderColor: "border-purple-400 dark:border-fuchsia-600",
  },
  agent_task: {
    icon: Bot,
    label: "Agent Task",
    color: "indigo",
    bgColor: "bg-indigo-50",
    darkBgColor: "dark:bg-indigo-950/40",
    iconColor: "text-purple-700 dark:text-indigo-400",
    borderColor: "border-indigo-400 dark:border-indigo-600",
  },
  agent_decision: {
    icon: GitMerge,
    label: "Agent Decision",
    color: "violet",
    bgColor: "bg-violet-50",
    darkBgColor: "dark:bg-violet-950/40",
    iconColor: "text-violet-600 dark:text-violet-400",
    borderColor: "border-violet-400 dark:border-violet-600",
  },
  checkpoint: {
    icon: UserCheck,
    label: "Checkpoint",
    color: "amber",
    bgColor: "bg-amber-50",
    darkBgColor: "dark:bg-amber-950/40",
    iconColor: "text-amber-600 dark:text-orange-400",
    borderColor: "border-amber-400 dark:border-orange-600",
  },
  mcp_tool_call: {
    icon: Wrench,
    label: "MCP Tool",
    color: "blue",
    bgColor: "bg-blue-50",
    darkBgColor: "dark:bg-cyan-950/40",
    iconColor: "text-blue-600 dark:text-cyan-400",
    borderColor: "border-blue-400 dark:border-cyan-600",
  },
  kb_source: {
    icon: BookOpen,
    label: "KB Source",
    color: "emerald",
    bgColor: "bg-emerald-50",
    darkBgColor: "dark:bg-emerald-950/40",
    iconColor: "text-emerald-700 dark:text-emerald-400",
    borderColor: "border-emerald-400 dark:border-emerald-600",
  },
  kb_output: {
    icon: BookPlus,
    label: "KB Output",
    color: "teal",
    bgColor: "bg-teal-50",
    darkBgColor: "dark:bg-teal-950/40",
    iconColor: "text-teal-700 dark:text-teal-400",
    borderColor: "border-teal-400 dark:border-teal-600",
  },
  kg_search: {
    icon: Network,
    label: "KG Search",
    color: "cyan",
    bgColor: "bg-cyan-50",
    darkBgColor: "dark:bg-cyan-950/40",
    iconColor: "text-cyan-700 dark:text-cyan-400",
    borderColor: "border-cyan-400 dark:border-cyan-600",
  },
  kg_neighbors: {
    icon: GitFork,
    label: "KG Neighbors",
    color: "sky",
    bgColor: "bg-sky-50",
    darkBgColor: "dark:bg-sky-950/40",
    iconColor: "text-sky-700 dark:text-sky-400",
    borderColor: "border-sky-400 dark:border-sky-600",
  },
  kg_context: {
    icon: ScanSearch,
    label: "KG Context",
    color: "indigo",
    bgColor: "bg-indigo-50",
    darkBgColor: "dark:bg-indigo-950/40",
    iconColor: "text-indigo-700 dark:text-indigo-400",
    borderColor: "border-indigo-400 dark:border-indigo-600",
  },
};

const catalogItemConfig: Partial<Record<WorkflowCatalogItemType, StepTypeConfig>> = {
  webhook_trigger: {
    icon: Webhook,
    label: "Webhook Trigger",
    color: "cyan",
    bgColor: "bg-cyan-50",
    darkBgColor: "dark:bg-cyan-950/40",
    iconColor: "text-cyan-700 dark:text-cyan-400",
    borderColor: "border-cyan-400 dark:border-cyan-600",
  },
};

export function getStepTypeConfig(stepType: WorkflowCatalogItemType): StepTypeConfig {
  return catalogItemConfig[stepType] || stepTypeConfig[stepType as StepType] || stepTypeConfig.code;
}

export function getStepTypeLabel(stepType: WorkflowCatalogItemType): string {
  return catalogItemConfig[stepType]?.label || stepTypeConfig[stepType as StepType]?.label || stepType;
}
